// 知识库中间的阅读区：只有正文。顶上一条很细的阅读进度；标题、一行来源、正文。
// 选中一句才出现小工具条：问 AI（带进右边的对话）、记为观点（存进「观点」，不调用模型）、复制。
// 从「观点」的「在知识库里定位」来时地址里带着 ?hl=<引用>：滚到那一段并标出来。

import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App as AntApp } from 'antd'
import { Copy, ExternalLink, Quote, Sparkles } from 'lucide-react'
import { getKnowledge } from '../../api/kb'
import { markClaim } from '../../api/insights'
import type { Knowledge } from '../../api/types'
import { DocBody } from './DocSheet'
import { docTitle, shortDate, sourceLabel } from './DocCard'

const MAX_QUOTE = 400

/** 找到引用所在的那一段，标出来并滚过去（比较时不管空白和 Markdown 记号；先用开头一长截找，找不到再缩短）。 */
function flash(root: Element, quote: string): boolean {
  const plain = (s: string) => s.replace(/[\s*_`#>|~\-「」“”"]+/g, '')
  const want = plain(quote)
  for (const len of [30, 14, 8]) {
    const probe = want.slice(0, len)
    if (probe.length < 4) return false
    const walk = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
    for (let n = walk.nextNode(); n; n = walk.nextNode()) {
      const block = n.parentElement?.closest('p, li, blockquote, h1, h2, h3, h4, td')
      if (!block || !plain(block.textContent ?? '').includes(probe)) continue
      root.querySelectorAll('.rd-flash').forEach((x) => x.classList.remove('rd-flash'))
      block.classList.add('rd-flash')
      block.scrollIntoView?.({ block: 'center' })
      return true
    }
  }
  return false
}

export function useDoc(docId?: string) {
  return useQuery({ queryKey: ['knowledge-item', docId], enabled: !!docId, queryFn: () => getKnowledge(docId!) })
}

export function DocReader({ docId, onAsk }: { docId: string; onAsk: (quote: string) => void }) {
  const doc = useDoc(docId)
  const qc = useQueryClient()
  const { message } = AntApp.useApp()
  const box = useRef<HTMLDivElement>(null)
  const [progress, setProgress] = useState(0)
  const [sel, setSel] = useState<{ text: string; top: number; left: number } | null>(null)
  const hl = useSearchParams()[0].get('hl')

  useEffect(() => {
    setSel(null)
    setProgress(0)
    box.current?.scrollTo?.({ top: 0 })
  }, [docId])

  const mark = useMutation({
    mutationFn: (quote: string) => markClaim({ knowledgeId: docId, quote }),
    onSuccess: () => {
      void message.success('记下了 · 在「观点」里')
      qc.invalidateQueries({ queryKey: ['doc-insights', docId] })
      qc.invalidateQueries({ queryKey: ['insight-docs'] })
      setSel(null)
      window.getSelection()?.removeAllRanges()
    },
    onError: (e) => void message.error(e.message),
  })

  const pick = () => {
    const s = window.getSelection()
    const text = s?.toString().replace(/\s+\n/g, '\n').trim() ?? ''
    const el = box.current
    if (!s || !el || s.rangeCount === 0 || text.length < 4 || !el.contains(s.anchorNode)) return setSel(null)
    const range = s.getRangeAt(0)
    const r = typeof range.getBoundingClientRect === 'function' ? range.getBoundingClientRect() : { top: 0, left: 0 }
    const b = el.getBoundingClientRect()
    // 手机上系统自己的「拷贝」菜单在选区上面：工具条放到选区下面
    const below = matchMedia('(pointer: coarse)').matches
    const bottom = 'bottom' in r ? r.bottom : r.top
    setSel({ text, top: below ? bottom - b.top + el.scrollTop + 12 : r.top - b.top + el.scrollTop - 44, left: Math.max(12, Math.min(r.left - b.left, b.width - 260)) })
  }
  // 手指长按选字不会有 mouseup：跟着选区变化，停下来 0.3 秒再出工具条
  useEffect(() => {
    if (!matchMedia('(pointer: coarse)').matches) return
    let t: ReturnType<typeof setTimeout>
    const onChange = () => {
      clearTimeout(t)
      t = setTimeout(pick, 300)
    }
    document.addEventListener('selectionchange', onChange)
    return () => {
      clearTimeout(t)
      document.removeEventListener('selectionchange', onChange)
    }
  }, [])

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      void message.success('已复制')
    } catch {
      void message.error('没能复制，浏览器不让读写剪贴板')
    }
  }

  const d = doc.data
  // 正文可能还在载入：隔一会儿再找，最多找三秒
  useEffect(() => {
    if (!hl || !d) return
    let tries = 0
    const t = setInterval(() => {
      const root = box.current?.querySelector('.rd-inner')
      if ((root && flash(root, hl)) || ++tries > 20) clearInterval(t)
    }, 150)
    return () => clearInterval(t)
  }, [hl, d?.id])
  return (
    <article className="rd" aria-label="阅读">
      <div className="rd-progress" aria-hidden="true">
        <i style={{ width: `${Math.round(progress * 100)}%` }} />
      </div>
      <div
        ref={box}
        className="rd-scroll fade-edges"
        onMouseUp={pick}
        onKeyUp={pick}
        onScroll={(e) => {
          const t = e.currentTarget
          const max = t.scrollHeight - t.clientHeight
          setProgress(max > 0 ? t.scrollTop / max : 1)
        }}
      >
        {sel && (
          <div className="rd-tools" role="toolbar" aria-label="选中的文字" style={{ top: sel.top, left: sel.left }} onMouseUp={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => { onAsk(sel.text.slice(0, MAX_QUOTE)); setSel(null) }}>
              <Sparkles aria-hidden="true" />
              问 AI
            </button>
            <button type="button" disabled={mark.isPending} onClick={() => (sel.text.length > MAX_QUOTE ? void message.warning(`一次最多记 ${MAX_QUOTE} 字，少选一点`) : mark.mutate(sel.text))}>
              <Quote aria-hidden="true" />
              记为观点
            </button>
            <button type="button" onClick={() => void copy(sel.text)}>
              <Copy aria-hidden="true" />
              复制
            </button>
          </div>
        )}
        <div className="rd-inner">
          {doc.isError && <p className="rd-note">没能打开这篇资料：{doc.error.message}</p>}
          {doc.isPending && <div className="rd-wait" aria-busy="true" />}
          {d && <ReaderHead doc={d} />}
          {d && <DocBody doc={d} />}
        </div>
      </div>
    </article>
  )
}

function ReaderHead({ doc }: { doc: Knowledge }) {
  const source = doc.source && /^https?:\/\//.test(doc.source) ? doc.source : null
  return (
    <header className="rd-head">
      <h1>{docTitle(doc)}</h1>
      <p className="rd-meta">
        <span>{sourceLabel(doc)}</span>
        <span>{shortDate(doc.created_at)} 收进</span>
        {doc.folder_path && <span>{doc.folder_path}</span>}
        {source && (
          <a href={source} target="_blank" rel="noopener noreferrer">
            <ExternalLink width={13} height={13} aria-hidden="true" />
            原网页
          </a>
        )}
      </p>
    </header>
  )
}
